import { Platform } from 'react-native';
import { authApiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  KioskActivationStatus,
  KioskAuthSession,
  KioskRecord,
  StoredKioskSession,
} from '../types/kiosk';
import {
  clearPersistedKioskSession,
  clearKioskTokens,
  getKioskAccessToken,
  getKioskRefreshToken,
  getKioskSession,
  getOrCreateDeviceIdentifier,
  hasActivatedKioskSession,
  loadPersistedKioskSession,
  persistKioskSession,
  setKioskTokens,
} from './kioskTokenStore';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

let refreshPromise: Promise<string> | null = null;
let restorePromise: Promise<StoredKioskSession | null> | null = null;
let sessionInvalidated: ((message: string) => void) | null = null;

export function registerKioskSessionInvalidationHandler(
  handler: ((message: string) => void) | null,
) {
  sessionInvalidated = handler;
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function requiredId(value: unknown, label: string): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed <= 0) {
    throw Object.assign(new Error(`${label} is missing`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function requiredText(value: unknown, label: string): string {
  const text = textOrNull(value);
  if (!text) {
    throw Object.assign(new Error(`${label} is required`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return text;
}

function normalizeKiosk(row: ObjectValue): KioskRecord {
  return {
    kioskId: requiredId(row.kioskId, 'kioskId'),
    kioskUuid: textOrNull(row.kioskUuid),
    organizationId: requiredId(row.organizationId, 'organizationId'),
    locationId: numberOrNull(row.locationId),
    kioskName: textOrNull(row.kioskName) ?? 'Kiosk',
    deviceId: textOrNull(row.deviceId),
    deviceName: textOrNull(row.deviceName),
    deviceIdentifier: textOrNull(row.deviceIdentifier),
    status: String(row.status || '').toUpperCase(),
    activatedAt: textOrNull(row.activatedAt),
    lastSeenAt: textOrNull(row.lastSeenAt),
    activationTokenExpiresAt: textOrNull(row.activationTokenExpiresAt),
    createdAt: textOrNull(row.createdAt),
    updatedAt: textOrNull(row.updatedAt),
    rowVersion: numberOrNull(row.rowVersion),
  };
}

function normalizeAuthSession(data: ObjectValue): KioskAuthSession {
  const kiosk = normalizeKiosk((data.kiosk as ObjectValue) || {});
  return {
    kiosk,
    accessToken: requiredText(data.accessToken, 'accessToken'),
    refreshToken: requiredText(data.refreshToken, 'refreshToken'),
    refreshTokenExpiresAt: textOrNull(data.refreshTokenExpiresAt),
    tokenType: textOrNull(data.tokenType) ?? 'Bearer',
  };
}

function normalizeActivationStatus(data: ObjectValue): KioskActivationStatus {
  const status = String(data.status || '').toUpperCase();
  return {
    kioskId: requiredId(data.kioskId, 'kioskId'),
    organizationId: requiredId(data.organizationId, 'organizationId'),
    status,
    activatedAt: textOrNull(data.activatedAt),
    activationTokenExpiresAt: textOrNull(data.activationTokenExpiresAt),
    isActivated:
      data.isActivated === true ||
      data.isActivated === 1 ||
      status === 'ACTIVE',
    isPending:
      data.isPending === true ||
      data.isPending === 1 ||
      status === 'PENDING',
  };
}

function deviceNameFallback(): string {
  return Platform.select({
    ios: 'iOS Kiosk',
    android: 'Android Kiosk',
    default: 'Kiosk Device',
  }) as string;
}

function deviceHeaders(deviceIdentifier: string) {
  return {
    'x-device-id': deviceIdentifier.slice(0, 255),
    'x-device-name': deviceNameFallback().slice(0, 255),
    'x-device-type': Platform.OS === 'web' ? 'WEB' : 'MOBILE',
    'x-os-name': Platform.OS,
  };
}

async function saveAuthSession(
  session: KioskAuthSession,
  deviceIdentifier: string,
): Promise<StoredKioskSession> {
  const stored: StoredKioskSession = {
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
    kioskId: session.kiosk.kioskId,
    organizationId: session.kiosk.organizationId,
    deviceIdentifier,
    kioskName: session.kiosk.kioskName,
  };
  await persistKioskSession(stored);
  return stored;
}

/**
 * Restore persisted kiosk JWT session into memory (no network).
 */
export async function restoreKioskSession(): Promise<StoredKioskSession | null> {
  if (hasActivatedKioskSession()) {
    return getKioskSession();
  }
  if (!restorePromise) {
    restorePromise = (async () => {
      const session = await loadPersistedKioskSession();
      return session && hasActivatedKioskSession() ? session : null;
    })().finally(() => {
      restorePromise = null;
    });
  }
  return restorePromise;
}

/**
 * True when this device has an activated kiosk session ready for attendance APIs.
 */
export function isKioskActivated(): boolean {
  return hasActivatedKioskSession();
}

export function getActiveKioskSession(): StoredKioskSession | null {
  return hasActivatedKioskSession() ? getKioskSession() : null;
}

/**
 * Establish kiosk session at startup from secure storage.
 * Does not use env credentials or static tokens.
 */
export async function ensureKioskSession(): Promise<string> {
  const existing = getKioskAccessToken();
  if (existing && hasActivatedKioskSession()) return existing;

  const restored = await restoreKioskSession();
  if (restored?.accessToken) return restored.accessToken;

  throw Object.assign(new Error('Kiosk is not activated on this device'), {
    code: 'KIOSK_NOT_ACTIVATED',
  });
}

export async function activateKioskWithToken(input: {
  activationToken: string;
  deviceName?: string | null;
}): Promise<StoredKioskSession> {
  const activationToken = input.activationToken.trim();
  if (!activationToken) {
    throw Object.assign(new Error('Activation token is required'), {
      code: 'VALIDATION_ERROR',
    });
  }
  const deviceIdentifier = await getOrCreateDeviceIdentifier();
  const response = await authApiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.KIOSKS.ACTIVATE,
    {
      activationToken,
      deviceIdentifier,
      deviceName: input.deviceName?.trim() || deviceNameFallback(),
    },
    { headers: deviceHeaders(deviceIdentifier) },
  );
  const session = normalizeAuthSession(response.data.data ?? {});
  return saveAuthSession(session, deviceIdentifier);
}

export async function getPublicActivationStatus(
  activationToken: string,
): Promise<KioskActivationStatus> {
  const token = activationToken.trim();
  if (!token) {
    throw Object.assign(new Error('Activation token is required'), {
      code: 'VALIDATION_ERROR',
    });
  }
  const response = await authApiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.KIOSKS.ACTIVATION_STATUS,
    { params: { activationToken: token } },
  );
  return normalizeActivationStatus(response.data.data ?? {});
}

/**
 * Refresh kiosk access token via POST /kiosks/auth/refresh + x-device-id.
 */
export async function refreshKioskAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const currentRefresh = getKioskRefreshToken();
      const session = getKioskSession();
      const deviceIdentifier =
        session?.deviceIdentifier || (await getOrCreateDeviceIdentifier());

      if (!currentRefresh) {
        throw Object.assign(
          new Error('Kiosk session expired and cannot be renewed'),
          { code: 'KIOSK_SESSION_EXPIRED' },
        );
      }

      try {
        const response = await authApiClient.post<Envelope<ObjectValue>>(
          API_ENDPOINTS.KIOSKS.REFRESH,
          { refreshToken: currentRefresh },
          { headers: deviceHeaders(deviceIdentifier) },
        );
        const next = normalizeAuthSession(response.data.data ?? {});
        await saveAuthSession(next, deviceIdentifier);
        return next.accessToken;
      } catch (error) {
        await invalidateKioskSession(
          'This kiosk is no longer authorized. Activate it again.',
        );
        throw error;
      }
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

export async function invalidateKioskSession(
  message = 'This kiosk is no longer authorized. Activate it again.',
): Promise<void> {
  clearKioskTokens();
  await clearPersistedKioskSession();
  sessionInvalidated?.(message);
}

/** Keep in-memory tokens for employee login takeover without wiping Keychain. */
export function suspendKioskTokensForEmployeeLogin() {
  clearKioskTokens();
}

/** Reload Keychain session into memory after employee logout. */
export async function resumeKioskSessionAfterEmployeeLogout(): Promise<StoredKioskSession | null> {
  return restoreKioskSession();
}

/** Test helper: seed in-memory session without Keychain. */
export function seedKioskSessionForTests(session: StoredKioskSession) {
  setKioskTokens(session);
}
